import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { getCurrentSubdomain } from '../utils/subdomain';

const Layout = () => import('../components/Layout.vue');

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/subdomain',
      name: 'subdomain-input',
      component: () => import('../views/SubdomainInput.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/select-post',
      name: 'select-post',
      component: () => import('../views/SelectPostView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/',
      component: Layout,
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: '/dashboard' },
        {
          path: 'dashboard',
          name: 'security-dashboard',
          component: () => import('../views/SecurityDashboard.vue'),
          meta: { requiresUserType: 'SECURITY_GUARD' },
        },
        {
          path: 'visitors',
          name: 'visitors-list',
          component: () => import('../views/VisitorsListView.vue'),
          meta: { requiresUserType: 'SECURITY_GUARD' },
        },
        {
          path: 'vehicles',
          name: 'vehicle-gate',
          component: () => import('../views/VehicleGateView.vue'),
          meta: { requiresUserType: 'SECURITY_GUARD' },
        },
        {
          path: 'scan',
          name: 'scanner',
          component: () => import('../views/ScannerView.vue'),
          meta: { requiresUserType: 'SECURITY_GUARD' },
        },
        {
          path: 'alerts',
          name: 'alerts',
          component: () => import('../views/AlertsView.vue'),
          meta: { requiresUserType: 'SECURITY_GUARD' },
        },
        {
          path: 'incidents/report',
          name: 'incident-report',
          component: () => import('../views/IncidentReportView.vue'),
          meta: { requiresUserType: 'SECURITY_GUARD' },
        },
        {
          path: 'pass',
          name: 'staff-pass',
          component: () => import('../views/StaffPassView.vue'),
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('../views/ProfileView.vue'),
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

router.beforeEach(to => {
  const authStore = useAuthStore();

  const subdomain = getCurrentSubdomain();
  if (!subdomain && to.name !== 'subdomain-input') {
    return { name: 'subdomain-input' };
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login' };
  }

  // After login: security guards without a post are directed to select one (skip if already on that page)
  if (
    authStore.isAuthenticated &&
    authStore.isSecurityGuard &&
    !authStore.gatePost &&
    to.name !== 'select-post'
  ) {
    return { name: 'select-post' };
  }

  if (!to.meta.requiresAuth && authStore.isAuthenticated && to.name === 'login') {
    if (authStore.isSecurityGuard) {
      return authStore.hasSelectedPost ? { name: 'security-dashboard' } : { name: 'select-post' };
    }
    return { name: 'staff-pass' };
  }

  // Resident staff: skip post selection and go straight to their pass
  if (
    authStore.isAuthenticated &&
    authStore.isResidentStaff &&
    to.name !== 'staff-pass' &&
    to.name !== 'profile' &&
    to.meta.requiresUserType === 'SECURITY_GUARD'
  ) {
    return { name: 'staff-pass' };
  }

  if (
    to.meta.requiresUserType === 'SECURITY_GUARD' &&
    authStore.isAuthenticated &&
    !authStore.isSecurityGuard
  ) {
    return { name: 'staff-pass' };
  }

  return undefined;
});

export default router;
